import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, NotebookText, Pencil, Trash2 } from 'lucide-react';
import { cn } from 'lib/utils';
import { formatRelativeTime } from 'lib/relativeTime';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import type { Types } from 'modules/notebooks';

export interface NotebookCardProps {
  notebook: Types.Notebook;
  /** What the notebook is called on screen (the untouched default notebook is localised). */
  title: string;
  onRename: (notebook: Types.Notebook) => void;
  /** Left out when the user lacks the delete permission: the menu then offers Rename only. */
  onDelete?: (notebook: Types.Notebook) => void;
  className?: string;
}

/**
 * A 148px card: an ink-on-hover notebook tile, the title (the card's one strong line), an optional note,
 * and the source count with the update time at the foot. The card is one link; the menu is its sibling
 * (a button can't live inside an anchor) pinned over the card's top-right corner.
 */
export const NotebookCard: React.FC<NotebookCardProps> = ({ notebook, title, onRename, onDelete, className }) => {
  const { t, i18n } = useTranslation();
  const updatedLabel = notebook.updatedAt ? formatRelativeTime(notebook.updatedAt, i18n.language) : '';

  return (
    <li className={cn('group relative list-none', className)}>
      <Link
        to={`/app/documents/${notebook._id}`}
        className="flex h-full min-h-[148px] flex-col gap-3 rounded-lg border border-border bg-card p-4 text-left transition-[border-color,box-shadow] duration-150 hover:border-foreground/25 hover:shadow-sm focus-visible:border-foreground/40"
      >
        {/* Inverts to ink while the card is hovered or holds focus. */}
        <span
          aria-hidden
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground/80 transition-colors duration-150 [.group:focus-within_&]:bg-foreground [.group:focus-within_&]:text-background [.group:hover_&]:bg-foreground [.group:hover_&]:text-background"
        >
          <NotebookText className="h-[18px] w-[18px]" />
        </span>
        <span className="block min-w-0">
          <span className="line-clamp-2 text-[15px] font-semibold leading-[1.35] text-foreground [overflow-wrap:anywhere]">
            {title}
          </span>
          {notebook.description && (
            <span className="mt-1 line-clamp-1 text-[13px] leading-5 text-muted-foreground">{notebook.description}</span>
          )}
        </span>
        <span className="mt-auto flex flex-wrap items-center gap-x-1.5 text-[12.5px] tabular-nums text-muted-foreground">
          <span>{t('notebooks.sources_count', { count: notebook.documentCount })}</span>
          {updatedLabel && (
            <>
              <span aria-hidden>·</span>
              <span>{t('notebooks.updated_ago', { when: updatedLabel })}</span>
            </>
          )}
        </span>
      </Link>

      <div className="absolute right-2.5 top-2.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('notebooks.more_actions', { title })}
              title={t('more_options')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
            >
              <MoreHorizontal className="h-4 w-4" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem className="gap-2" onClick={() => onRename(notebook)}>
              <Pencil size={14} aria-hidden />
              {t('notebooks.rename')}
            </DropdownMenuItem>
            {onDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="gap-2 text-destructive focus:text-destructive"
                  onClick={() => onDelete(notebook)}
                >
                  <Trash2 size={14} aria-hidden />
                  {t('delete')}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  );
};

export default NotebookCard;
